import React, {useEffect, useState} from "react";
import {Modal} from "react-bootstrap";
import {genericGetOne} from "../generic/crud";
import {ENTITY_ID_DEALER, ENTITY_ID_BRANCH} from "../generic/Constants";
import {toAbsoluteUrl} from "../../../_metronic/helpers";
import axios from "axios";
import {getBrandImage} from "../generic/OEMList";

export function DealerDetailsDialog(props) {
    const {show, handleClose, dealerId} = props;
    const [dealer, setDealer] = useState(null);
    const [branches, setBranches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (dealerId && show) {
            setIsLoading(true);

            // Fetch dealer details
            genericGetOne(ENTITY_ID_DEALER, dealerId).then(
                ({data}) => {
                    setDealer(data);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });

            // Fetch branches for this dealer
            const apiUrl = process.env.REACT_APP_API_URL;
            axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_BRANCH,
                    page: 1,
                    sizePerPage: 100,
                    currentIndex: 0,
                    sortBy: 'name',
                    fixedFilterData: dealerId,
                    fixedFilterColumn: 'dealerId'
                }
            }).then(({data}) => {
                setBranches(data.entities || []);
            }).catch(() => {
                setBranches([]);
            });
        }
    }, [dealerId, show]);

    if (!dealer) {
        return null;
    }

    const brandImage = getBrandImage(dealer.brand);
    const brandImageUrl = toAbsoluteUrl(`/media/car/${brandImage}`);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            aria-labelledby="dealer-details-modal"
            centered
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title></Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0 pb-10">
                {isLoading && (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg text-primary"></span>
                        <p className="mt-3 text-gray-500">Loading dealer information...</p>
                    </div>
                )}

                {!isLoading && dealer && (
                    <div className="px-2">
                        {/* Dealer Profile Header */}
                        <div className="d-flex flex-column align-items-center mb-8">
                            <div className="symbol symbol-80px symbol-circle bg-light p-3 mb-4 shadow-sm border border-gray-200">
                                <img
                                    src={brandImageUrl}
                                    alt={dealer.brand}
                                    className="h-100 align-self-center object-fit-contain"
                                />
                            </div>
                            <h2 className="fw-bolder text-gray-900 mb-1">{dealer.name}</h2>
                            <div className="d-flex align-items-center mt-1">
                                <span className="badge badge-light-primary fw-bold px-3 py-2 fs-7 text-uppercase">
                                    {dealer.brand || 'Unknown Brand'}
                                </span>
                            </div>
                        </div>

                        {/* Dealer Information List */}
                        <div className="mb-8">
                            <h5 className="mb-4 fw-bold text-gray-700">Information</h5>
                            <div className="d-flex flex-column gap-3">
                                <div className="d-flex align-items-center justify-content-between p-3 rounded border border-gray-200 border-dashed">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-geo-alt fs-3 text-primary me-3"></i>
                                        <span className="text-gray-600 fw-semibold">Address</span>
                                    </div>
                                    <span className="text-gray-800 fw-bold text-end mw-200px">{dealer.address || '-'}</span>
                                </div>

                                <div className="d-flex align-items-center justify-content-between p-3 rounded border border-gray-200 border-dashed">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-building fs-3 text-success me-3"></i>
                                        <span className="text-gray-600 fw-semibold">City</span>
                                    </div>
                                    <span className="text-gray-800 fw-bold">{dealer.operatingCity || '-'}</span>
                                </div>

                                <div className="d-flex align-items-center justify-content-between p-3 rounded border border-gray-200 border-dashed">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-receipt fs-3 text-warning me-3"></i>
                                        <span className="text-gray-600 fw-semibold">GST</span>
                                    </div>
                                    <span className="text-gray-800 fw-bold">{dealer.gstNumber || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Branches Section */}
                        <div className="separator separator-dashed my-6"></div>

                        <div>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h5 className="fw-bold text-gray-900 m-0">
                                    Branches
                                    <span className="badge badge-circle badge-light-secondary ms-2 text-gray-600">
                                        {branches.length}
                                    </span>
                                </h5>
                            </div>

                            {branches.length === 0 ? (
                                <div className="text-center py-5 rounded bg-light border border-dashed border-gray-300">
                                    <i className="bi bi-info-circle fs-2 text-gray-400 mb-2 d-block"></i>
                                    <span className="text-muted fw-semibold">No branches found</span>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{maxHeight: '300px', overflowY: 'auto'}}>
                                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3">
                                        <thead className="sticky-top bg-body" style={{zIndex: 1}}>
                                            <tr className="fw-bold text-muted">
                                                <th className="min-w-100px ps-3">Branch Name</th>
                                                <th className="min-w-100px">Manager</th>
                                                <th className="min-w-100px pe-3">Supervisor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {branches.map((branch, index) => (
                                                <tr key={branch.id} className={index % 2 !== 0 ? 'bg-light-primary' : ''}>
                                                    <td className="ps-3">
                                                        <span className="text-gray-800 fw-bold fs-6">
                                                            {branch.name}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-gray-600 fw-semibold fs-7">
                                                            {branch.managerName || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="pe-3">
                                                        <span className="text-gray-600 fw-semibold fs-7">
                                                            {branch.supervisorName && branch.supervisorName.length > 0
                                                                ? (Array.isArray(branch.supervisorName) ? branch.supervisorName.join(', ') : branch.supervisorName)
                                                                : 'N/A'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
